import { useState, useRef } from "react";
import { Volume2, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * ListenButton
 *
 * mode="text"       → say the text as written (default).
 * mode="phoneme"    → the text is one grapheme, say the SOUND (e.g. "d" → /d/).
 * mode="sound_out"  → the text is a word, sound it out letter by letter,
 *                     then say the whole word ("d-o-g. dog.").
 *
 * TTS responses are cached server-side by (mode, voice, text) so repeated
 * clicks in a game round cost zero LLM credits after the first play.
 */
export default function ListenButton({
    text,
    voice = "sage",
    size = "md",
    label = "Listen",
    mode = "text",
    testId,
}) {
    const [loading, setLoading] = useState(false);
    const audioRef = useRef(null);

    const sizeClass = {
        sm: "px-3 py-1.5 text-sm",
        md: "px-4 py-2 text-base",
        lg: "px-5 py-2.5 text-lg",
    }[size];

    const handleClick = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const { data } = await api.post("/tts", { text, voice, mode });
            const audio = new Audio(`data:${data.mime};base64,${data.audio_base64}`);
            audioRef.current = audio;
            audio.play().catch(() => {});
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("TTS failed", e);
        } finally {
            setLoading(false);
        }
    };

    const aria =
        mode === "phoneme"
            ? `Play the sound of ${text}`
            : mode === "sound_out"
                ? `Sound out ${text}`
                : `Listen to ${text}`;

    return (
        <button
            onClick={handleClick}
            disabled={loading}
            data-testid={testId || "listen-button"}
            data-mode={mode}
            className={`inline-flex items-center gap-2 rounded-lg border border-[#E5E7E2] bg-white hover:bg-[#E8E2D2] text-[#2D5D4E] font-medium transition-all ${sizeClass} disabled:opacity-50`}
            aria-label={aria}
        >
            {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
                <Volume2 className="w-4 h-4" strokeWidth={1.8} />
            )}
            <span>{label}</span>
        </button>
    );
}

